import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiBriefcase,
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiX,
  FiExternalLink,
  FiMail,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiSmartphone,
  FiGlobe,
} from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { usePermission } from "../../context/PermissionContext";
import { safeUrl } from "../../utils/safeUrl";
import {
  fetchSponsorApplications,
  fetchSponsorApplication,
  updateSponsorApplication,
  deleteSponsorApplication,
} from "../../utils/sponsorApplicationApi";

/**
 * Sponsor applications — the website sponsor form's review queue.
 *
 * Status and the note are internal bookkeeping; the team contacts the
 * applicant themselves (the detail view has an email button for that).
 * Delete is for spam — a real decline should be Rejected so it stays on record.
 */

const STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "in_review", label: "In Review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];
const TABS = [...STATUSES, { key: "all", label: "All" }];

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  in_review: "bg-blue-50 text-blue-700 border-blue-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const PACKAGES = { banner: "Banner Spotlight", task: "Featured Task", event: "Event Partner", custom: "Not sure / Custom" };
const TYPES = { app: "Mobile App", website: "Website", both: "App & Website" };

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const StatusBadge = ({ status, label }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold whitespace-nowrap ${
      STATUS_BADGE[status] || "bg-gray-50 text-gray-600 border-gray-200"
    }`}
  >
    {label || status}
  </span>
);

const TypeBadge = ({ type, label }) => {
  const Icon = type === "website" ? FiGlobe : FiSmartphone;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-medium whitespace-nowrap">
      <Icon size={10} /> {label || TYPES[type] || type}
    </span>
  );
};

/** Applicant-supplied link: shown as text, clickable only when safeUrl allows it. */
const OutLink = ({ href, children }) => {
  if (!href) return <span className="text-gray-300">—</span>;
  return (
    <a
      href={safeUrl(href)}
      target="_blank"
      rel="noreferrer noopener"
      className="text-blue-600 hover:underline break-all inline-flex items-start gap-1"
    >
      <span>{children || href}</span> <FiExternalLink size={11} className="shrink-0 mt-1" />
    </a>
  );
};

const Field = ({ label, children, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
    <div className="text-sm text-gray-800 break-words">
      {children === null || children === undefined || children === "" ? <span className="text-gray-300">—</span> : children}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h4 className="text-xs font-bold text-gray-700 mb-3">{title}</h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
  </section>
);

/** One application, with the review controls the admin's permissions allow. */
const DetailModal = ({ id, canEdit, canDelete, onClose, onChanged }) => {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchSponsorApplication(id)
      .then((res) => {
        if (!alive) return;
        setApp(res.data);
        setStatus(res.data.status);
        setNote(res.data.admin_note || "");
      })
      .catch((err) => {
        if (!alive) return;
        toast.error(err.message || "Could not load the application");
        onClose();
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, onClose]);

  const dirty = app && (status !== app.status || note.trim() !== (app.admin_note || ""));

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateSponsorApplication(id, { status, admin_note: note.trim() });
      toast.success(res.message || "Saved");
      setApp(res.data);
      setNote(res.data.admin_note || "");
      onChanged();
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${app.reference}? Use this for spam only. To decline an application, mark it Rejected.`)) return;
    setDeleting(true);
    try {
      const res = await deleteSponsorApplication(id);
      toast.success(res.message || "Deleted");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.message || "Could not delete");
      setDeleting(false);
    }
  };

  const mailto = app
    ? `mailto:${app.email}?subject=${encodeURIComponent(`Your PX Network sponsorship application ${app.reference}`)}`
    : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Sponsor application"
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-100">
          {app ? (
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-gray-800 break-words">{app.product_name}</h3>
                <StatusBadge status={app.status} label={app.status_label} />
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                <span className="font-mono">{app.reference}</span> · submitted {formatDate(app.created_at)}
              </p>
            </div>
          ) : (
            <h3 className="text-base font-bold text-gray-800">Application</h3>
          )}
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg shrink-0"
          >
            <FiX size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {loading || !app ? (
            <div className="flex justify-center py-12">
              <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <Section title="Applicant">
                <Field label="Full Name">{app.full_name}</Field>
                <Field label="Business Email">
                  <a href={mailto} className="text-blue-600 hover:underline break-all">
                    {app.email}
                  </a>
                  {app.other_applications_from_email > 0 && (
                    <div className="text-[11px] text-amber-700 mt-0.5">
                      {app.other_applications_from_email} other application
                      {app.other_applications_from_email === 1 ? "" : "s"} from this email
                    </div>
                  )}
                </Field>
                <Field label="Country">{app.country}</Field>
                <Field label="Phone Number">{app.phone}</Field>
              </Section>

              <Section title="App / Website">
                <Field label="What they want to sponsor">
                  <TypeBadge type={app.sponsor_type} label={app.sponsor_type_label} />
                </Field>
                <Field label="App / Website Name">{app.product_name}</Field>
                <Field label="App Store / Play Store URL">{app.app_url && <OutLink href={app.app_url} />}</Field>
                <Field label="Website URL">{app.website_url && <OutLink href={app.website_url} />}</Field>
              </Section>

              <Section title="Campaign">
                <Field label="Sponsorship Package">{app.sponsor_package_label}</Field>
                <Field label="Campaign Duration">{app.duration}</Field>
                <Field label="Target Regions">{app.target_regions}</Field>
                <Field label="Estimated Budget (USD)">{app.budget || "Prefer not to say"}</Field>
                <Field label="Campaign Goals & Details" wide>
                  <div className="whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-xl p-3 text-[13px] leading-relaxed">
                    {app.campaign_details}
                  </div>
                </Field>
              </Section>

              <Section title="Submission">
                <Field label="Requirements confirmed">{formatDate(app.terms_accepted_at)}</Field>
                <Field label="IP Address">{app.ip_address && <span className="font-mono text-xs">{app.ip_address}</span>}</Field>
                <Field label="Last reviewed">
                  {app.reviewed_by ? `${formatDate(app.reviewed_at)} by ${app.reviewed_by.email || `#${app.reviewed_by.id}`}` : null}
                </Field>
                <Field label="Browser">{app.user_agent && <span className="text-xs text-gray-500">{app.user_agent}</span>}</Field>
              </Section>

              {canEdit ? (
                <section className="rounded-xl border border-gray-100 bg-gray-50/60 p-4 space-y-3">
                  <h4 className="text-xs font-bold text-gray-700">Review</h4>
                  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Status">
                    {STATUSES.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        role="radio"
                        aria-checked={status === s.key}
                        onClick={() => setStatus(s.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                          status === s.key
                            ? STATUS_BADGE[s.key]
                            : "bg-white text-gray-500 border-gray-200 hover:bg-gray-100"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <label className="block">
                    <span className="text-xs font-semibold text-gray-600">Internal note</span>
                    <textarea
                      rows={3}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={2000}
                      placeholder="Only staff see this, e.g. why it was declined or who is talking to them"
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </label>
                </section>
              ) : (
                app.admin_note && (
                  <Field label="Internal note" wide>
                    <span className="whitespace-pre-wrap">{app.admin_note}</span>
                  </Field>
                )
              )}
            </>
          )}
        </div>

        {app && !loading && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex flex-wrap items-center gap-2">
            {canDelete && (
              <button
                onClick={remove}
                disabled={deleting}
                className="px-3 py-2 text-xs font-semibold text-red-600 rounded-xl hover:bg-red-50 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <FiTrash2 size={13} /> {deleting ? "Deleting…" : "Delete (spam)"}
              </button>
            )}
            <div className="flex-1" />
            <a
              href={mailto}
              className="px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 inline-flex items-center gap-1.5"
            >
              <FiMail size={13} /> Email applicant
            </a>
            {canEdit && (
              <button
                onClick={save}
                disabled={!dirty || saving}
                className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-40"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SponsorApplications = () => {
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("sponsor-application", "edit");
  const canDelete = hasPermission("sponsor-application", "delete");

  const [status, setStatus] = useState("pending");
  const [pkg, setPkg] = useState("");
  const [type, setType] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSponsorApplications({ status, pkg, type, search, page, limit: 25 });
      setRows(res.data || []);
      setCounts(res.counts || {});
      setPagination(res.pagination || { current_page: 1, total_pages: 1, total: 0 });
    } catch (err) {
      toast.error(err.message || "Could not load sponsor applications");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, pkg, type, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Search on a short pause rather than per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const closeDetail = useCallback(() => setOpenId(null), []);

  const totalAll = STATUSES.reduce((sum, s) => sum + (counts[s.key] || 0), 0);
  const tabLabel = TABS.find((t) => t.key === status)?.label || "";

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiBriefcase className="text-blue-600" /> Sponsor Applications
                </h1>
                <p className="text-xs text-gray-400 mt-1">
                  Apps and websites that applied through the sponsor form on pxmine.com. Check each one against the
                  sponsor requirements before approving.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  aria-label="Package"
                  value={pkg}
                  onChange={(e) => {
                    setPkg(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700"
                >
                  <option value="">All packages</option>
                  {Object.entries(PACKAGES).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Type"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700"
                >
                  <option value="">All types</option>
                  {Object.entries(TYPES).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Reference, name, email, app"
                    className="pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-xs w-60 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <button
                  onClick={load}
                  className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50"
                  title="Refresh"
                  aria-label="Refresh"
                >
                  <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-1 px-4 py-3 border-b border-gray-100 bg-gray-50/50 overflow-x-auto">
                {TABS.map((tab) => {
                  const count = tab.key === "all" ? totalAll : counts[tab.key] || 0;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setStatus(tab.key);
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition flex items-center gap-1.5 ${
                        status === tab.key ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {tab.label}
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                          tab.key === "pending" && count > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3">Applicant</th>
                      <th className="text-left px-4 py-3">App / Website</th>
                      <th className="text-left px-4 py-3">Package</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Budget</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3 hidden lg:table-cell">Submitted</th>
                      <th className="text-right px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                          No {status === "all" ? "" : `${tabLabel.toLowerCase()} `}applications.
                        </td>
                      </tr>
                    )}
                    {rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/60 align-top">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{row.full_name}</div>
                          <div className="text-[10px] text-gray-400 break-all">{row.email}</div>
                          <div className="text-[10px] text-gray-400">{row.country}</div>
                        </td>
                        <td className="px-4 py-3 max-w-[240px]">
                          <div className="font-medium text-gray-800 truncate" title={row.product_name}>
                            {row.product_name}
                          </div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <TypeBadge type={row.sponsor_type} label={row.sponsor_type_label} />
                            {row.app_url && (
                              <a
                                href={safeUrl(row.app_url)}
                                target="_blank"
                                rel="noreferrer noopener"
                                title={row.app_url}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FiSmartphone size={12} />
                              </a>
                            )}
                            {row.website_url && (
                              <a
                                href={safeUrl(row.website_url)}
                                target="_blank"
                                rel="noreferrer noopener"
                                title={row.website_url}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FiGlobe size={12} />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-medium text-gray-700">{row.sponsor_package_label}</div>
                          <div className="text-[10px] text-gray-400">{row.duration}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap text-gray-600">
                          {row.budget || <span className="text-gray-300">Not said</span>}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={row.status} label={row.status_label} />
                          {row.reviewed_by && (
                            <div className="text-[10px] mt-1 text-gray-400" title={formatDate(row.reviewed_at)}>
                              by {row.reviewed_by.email || `#${row.reviewed_by.id}`}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell whitespace-nowrap">
                          <div className="text-gray-600">{formatDate(row.created_at)}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{row.reference}</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setOpenId(row.id)}
                            className="px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 font-semibold inline-flex items-center gap-1"
                          >
                            <FiEye size={12} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
                  <span>
                    Page {pagination.current_page} of {pagination.total_pages} · {pagination.total} applications
                  </span>
                  <div className="flex gap-1">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      aria-label="Previous page"
                      className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
                    >
                      <FiChevronLeft />
                    </button>
                    <button
                      disabled={page >= pagination.total_pages}
                      onClick={() => setPage((p) => p + 1)}
                      aria-label="Next page"
                      className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {openId && (
        <DetailModal id={openId} canEdit={canEdit} canDelete={canDelete} onClose={closeDetail} onChanged={load} />
      )}
    </div>
  );
};

export default SponsorApplications;
